import { defineComponent as _defineComponent } from 'vue'
import { Dictionary, groupBy, orderBy, sortBy } from 'lodash'
import FilterAsideSection from '@/components/common/FilterAsideSection.vue'
import FilterAsideDateRange from '@/components/common/FilterAsideDateRange.vue'
import PriorityFilter from '@/components/common/PriorityFilter.vue'
import TableSearch from '@/components/common/TableSearch.vue'
import JobsFilterLocationsAside from '@/components/jobs/JobsFilterLocationsAside.vue'
import { useJobFilterStore } from '@/components/field-view-next/JobsFilterStore'
import { computed, ref, toRef, watchEffect } from 'vue'
import { useGetJobFilterOptions } from '@/api/filter-options'
import { useGetters, useStore } from 'vuex-composition-helpers'
import { BatchStatus } from '@/models/invoice'
import { useScreen } from '@/common/useScreen'
import VsButton from '@/components/vision/VsButton.vue'
import { useFilters } from '@/components/jobs/filters'
import { useRoute } from 'vue-router/composables'
import { useFilteredJobStoreLegacyAdapter } from '@/components/jobs/job-search-page/filteredJobStore'


export default /*#__PURE__*/_defineComponent({
  __name: 'NewJobsFilterAside',
  props: {
    hideSearch: { type: Boolean }
  },
  setup(__props: any) {

const props = __props as {
  hideSearch?: boolean
}



const currentPanel = ref<'root' | 'locations'>('root')

const store = useJobFilterStore()
const legacyStore = useFilteredJobStoreLegacyAdapter()
const vueXStore = useStore()
const { isMobileView } = useScreen()
const filterAsideClass = computed(() => ({
  'filter-asise': true,
  'limit-width': !isMobileView.value,
}))
const route = useRoute()
const showArchivedButton = computed(
  () => route.name === 'job-search' && isMobileView.value
)

const { data: filterOptions, isFetched: filterOptionsIsFetched } =
  useGetJobFilterOptions()

const divisionOptions = computed(() => {
  const grouped = groupBy(
    filterOptions.value?.jobFilterOptions.divisionOptions,
    (d) => d.hubName
  )

  return withHeaders(grouped)
})

const resourceTypeOptions = computed(() => {
  const grouped = groupBy(
    filterOptions.value?.jobFilterOptions.resourceTypeOptions,
    (d) => d.serviceName
  )

  return withHeaders(grouped)
})

const activityOptions = computed(() => {
  const grouped = groupBy(
    filterOptions.value?.jobFilterOptions.activityOptions,
    (d) => d.hubName
  )

  return withHeaders(grouped)
})

const batchStatusOptions = [
  {
    id: BatchStatus.NotBatched,
    name: 'Not Batched',
  },
  {
    id: BatchStatus.Batched,
    name: 'Batched',
  },
]

const enableBatchStatusesFilter = computed<boolean>(
  () =>
    vueXStore.getters['featureFlags/invoices'] &&
    vueXStore.getters['permissions/getInvoicingEntitlement']
)

const hasApprovalStatusOptions = computed(
  () => filterOptions.value?.jobFilterOptions.approvalOptions.length
)

const withHeaders = function <T>(grouped: Dictionary<T[]>) {
  return orderBy(Object.keys(grouped))
    .map((groupKey) => [{ header: groupKey }, ...sortBy(grouped[groupKey])])
    .reduce((aggregate, items) => [...aggregate, ...items], [])
}

const { isHostOrganizationUser: worksWithMultipleServiceProviders } =
  useGetters('permissions', ['isHostOrganizationUser'])

const serviceProviderOptions = computed(() => {
  return [
    { id: null, name: 'No service provider' },
    ...(filterOptions?.value?.jobFilterOptions.serviceProviderOptions ?? []),
  ]
})

// self-healing filters: reset/clear any filter states that we are not showing
// to the user. This is likely only a problem for folks who log-in/out of
// with different user accounts and or different roles/permissions.
watchEffect(() => {
  if (!filterOptionsIsFetched.value) {
    return
  }

  // reset approval status filters if not shown to users
  if (
    !hasApprovalStatusOptions.value &&
    store.selectedApprovalStatuses.length > 0
  ) {
    store.$patch({ selectedApprovalStatuses: [] })
  }

  // reset service provider filters if not shown to users
  if (
    !worksWithMultipleServiceProviders.value &&
    store.selectedServiceProviders.length > 0
  ) {
    store.$patch({ selectedServiceProviders: [] })
  }

  // reset batch statuses filter if not shown to users
  if (!enableBatchStatusesFilter.value) {
    store.$patch({ selectedBatchStatuses: [] })
  }
})

const filters = useFilters()
const showArchived = toRef(filters, 'archived')

const updateServiceProvider = (serviceProviders: (string | null)[]) => {
  const options = filterOptions.value?.jobFilterOptions.serviceProviderOptions
  const hasNull = serviceProviders.includes(null)
  // filter out null if it exists
  const filteredIds = hasNull
    ? serviceProviders.filter((id) => id !== null)
    : serviceProviders
  const selectedServiceProviders = options?.filter((sp) =>
    filteredIds.includes(sp.id)
  )
  const selectedProviders = selectedServiceProviders?.map((sp) => {
    return {
      label: sp.name,
      value: sp.id,
    }
  })

  if (hasNull) {
    selectedProviders?.push({
      label: 'Unassigned',
      value: 'null',
    })
  }
  legacyStore.$patch({
    selectedServiceProviders: selectedProviders ?? [],
  })
}

const updateDivision = (divisions: string[]) => {
  const divisionOptions = filterOptions.value?.jobFilterOptions.divisionOptions
  const filteredDivisions = divisionOptions?.filter((d) =>
    divisions.includes(d.id)
  )
  const selectedDivisions = filteredDivisions?.map((d) => {
    return {
      label: d.name,
      value: d.id,
    }
  })
  legacyStore.$patch({
    selectedDivisions: selectedDivisions ?? [],
  })
}

const updateResourceType = (resourceTypes: number[]) => {
  const resourceTypeOptions =
    filterOptions.value?.jobFilterOptions.resourceTypeOptions
  const filteredResourceTypes = resourceTypeOptions?.filter((rt) =>
    resourceTypes.includes(rt.id)
  )
  const selectedResourceTypes = filteredResourceTypes?.map((rt) => {
    return {
      label: rt.name,
      value: rt.id,
    }
  })
  legacyStore.$patch({
    selectedResourceTypes: selectedResourceTypes ?? [],
  })
}

const updateActivity = (activities: number[]) => {
  const activityOptions = filterOptions.value?.jobFilterOptions.activityOptions
  const filteredActivities = activityOptions?.filter((a) =>
    activities.includes(a.id)
  )
  const selectedActivities = filteredActivities?.map((a) => {
    return {
      label: a.name,
      value: a.id,
    }
  })
  legacyStore.$patch({
    selectedActivities: selectedActivities ?? [],
  })
}

const updateJobStatus = (jobStatuses: string[]) => {
  const existingApprovalStatuses = legacyStore.selectedApprovalStatuses.value

  const jobStatusOptions =
    filterOptions.value?.jobFilterOptions.jobStatusOptions

  const approvalStatusOptions =
    filterOptions.value?.jobFilterOptions.approvalOptions

  const filteredJobStatuses = jobStatusOptions?.filter((js) =>
    jobStatuses.includes(js.id)
  )

  const filteredApprovalStatuses = approvalStatusOptions?.filter((as) =>
    existingApprovalStatuses.includes(as.id)
  )
  const selectedApprovalStatuses = filteredApprovalStatuses?.map((as) => {
    return {
      label: as.name,
      value: as.id,
    }
  })

  const selectedJobStatuses = filteredJobStatuses?.map((js) => {
    return {
      label: js.name,
      value: js.id,
    }
  })

  const combinedStatus = [
    ...(selectedJobStatuses ?? []),
    ...(selectedApprovalStatuses ?? []),
  ]

  legacyStore.$patch({
    selectedJobStatuses: combinedStatus,
  })
}

const updateApprovalStatus = (approvalStatuses: string[]) => {
  const existingJobStatuses = legacyStore.selectedJobStatuses.value

  const approvalStatusOptions =
    filterOptions.value?.jobFilterOptions.approvalOptions

  const jobStatusOptions =
    filterOptions.value?.jobFilterOptions.jobStatusOptions

  const filteredJobStatuses = jobStatusOptions?.filter((js) =>
    existingJobStatuses.includes(js.id)
  )
  const selectedJobStatuses = filteredJobStatuses?.map((js) => {
    return {
      label: js.name,
      value: js.id,
    }
  })

  const filteredApprovalStatuses = approvalStatusOptions?.filter((as) =>
    approvalStatuses.includes(as.id)
  )

  const selectedApprovalStatuses = filteredApprovalStatuses?.map((as) => {
    return {
      label: as.name,
      value: as.id,
    }
  })

  const combinedStatus = [
    ...(selectedJobStatuses ?? []),
    ...(selectedApprovalStatuses ?? []),
  ]

  legacyStore.$patch({
    selectedApprovalStatuses: combinedStatus,
  })
}

const updateLocations = (locations: number[]) => {
  legacyStore.selectedLocations.value = locations
  currentPanel.value = 'root'
}

return { __sfc: true,props, currentPanel, store, legacyStore, vueXStore, isMobileView, filterAsideClass, route, showArchivedButton, filterOptions, filterOptionsIsFetched, divisionOptions, resourceTypeOptions, activityOptions, batchStatusOptions, enableBatchStatusesFilter, hasApprovalStatusOptions, withHeaders, worksWithMultipleServiceProviders, serviceProviderOptions, filters, showArchived, updateServiceProvider, updateDivision, updateResourceType, updateActivity, updateJobStatus, updateApprovalStatus, updateLocations, FilterAsideSection, FilterAsideDateRange, PriorityFilter, TableSearch, JobsFilterLocationsAside, VsButton }
}

})